import type { CSSProperties, ReactNode } from "react";

import data from "@/components/guide/explainer-data.json";
import scene from "@/components/guide/hero-scene.json";
import { cn } from "@/lib/utils";

/**
 * The figures beside the place-recognition chapter, drawn from what the kit's own code
 * computed on the hero maze (tools/build_hero_explainers.py): real keypoints on a real frame,
 * real descriptors in the real codebook's clusters, the real similarity of one frame to every
 * other, the baseline's real graph over the frames at their true positions. Each animates once
 * when it appears -- keypoints popping in, points taking their cluster's colour, arrows
 * drawing, a path being traced -- so the reader sees the operation, not only its result.
 * Reduced motion shows the finished figure.
 */

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
/** Six visual words, six colours that stay apart in both themes. */
const WORD = ["#e11d48", "#f59e0b", "#10b981", "#0ea5e9", "#8b5cf6", "#14b8a6"];

function delay(ms: number): CSSProperties {
  return { animationDelay: `${ms}ms` };
}

function Figure({ children, caption, className }: { children: ReactNode; caption: ReactNode; className?: string }) {
  return (
    <figure className={cn("bg-card min-w-0 rounded-lg border p-4", className)}>
      {children}
      <figcaption className="text-muted-foreground mt-3 text-xs leading-5">{caption}</figcaption>
    </figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** The keys the keyboard agent listens for. */
export function Keycaps() {
  const key = (label: string, extra = "") => (
    <kbd className={cn("bg-muted inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-2 font-mono text-sm shadow-[inset_0_-2px_0_var(--border)]", extra)}>
      {label}
    </kbd>
  );
  return (
    <Figure caption="Each arrow press is one tick. Two arrows held together drive an arc. Space checks in; escape quits without a score.">
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-4 text-sm">
        <div className="grid grid-cols-3 gap-1">
          <span />
          {key("↑")}
          <span />
          {key("←")}
          {key("↓")}
          {key("→")}
        </div>
        <p>
          <span className="font-medium">Move.</span>{" "}
          <span className="text-muted-foreground">Forward, back, turn left, turn right.</span>
        </p>
        {key("space", "min-w-40")}
        <p>
          <span className="font-medium">Check in.</span>{" "}
          <span className="text-muted-foreground">Score the run at this position and end it.</span>
        </p>
        {key("esc")}
        <p>
          <span className="font-medium">Quit.</span>{" "}
          <span className="text-muted-foreground">End the run without a score.</span>
        </p>
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** The question: one frame from now, frames from before -- which was taken from here? */
export function ProblemExplainer() {
  const { matches, worst } = data.retrieval;
  const candidates = [matches[1], worst, matches[0], matches[2]];
  return (
    <Figure caption="One frame from the camera now, and four of the hundreds recorded on earlier drives. Two were taken from about where the robot is now; two were not. Telling which is the job.">
      <div className="grid grid-cols-[1fr_auto_2fr] items-center gap-3">
        <figure className="min-w-0">
          <img src={`${BASE}${data.frame.image}`} alt="" className="explainer-fade block w-full rounded" />
          <figcaption className="text-muted-foreground mt-1 text-[11px]">now</figcaption>
        </figure>
        <span className="text-muted-foreground text-xl">≟</span>
        <div className="grid grid-cols-2 gap-2">
          {candidates.map((m, i) => (
            <img key={m.index} src={`${BASE}${m.image}`} alt="" className="explainer-fade block w-full rounded" style={delay(300 + i * 180)} />
          ))}
        </div>
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** Two frames of the same place, and the pixel difference between them. */
export function DiffExplainer() {
  const { diff, frame } = data;
  const tiles = [
    { src: frame.image, label: "now" },
    { src: diff.other, label: "same place, another drive" },
    { src: diff.difference, label: `pixel difference, mean ${diff.mean_abs} of 255` },
  ];
  return (
    <Figure caption="Subtracting one frame from the other pixel by pixel leaves almost the whole picture. The camera moved a little, so nearly every pixel changed. Pixels are the wrong thing to compare.">
      <div className="grid grid-cols-3 gap-2">
        {tiles.map((t, i) => (
          <figure key={t.label} className="explainer-fade min-w-0" style={delay(200 + i * 500)}>
            <img src={`${BASE}${t.src}`} alt="" className="block w-full rounded" />
            <figcaption className="text-muted-foreground mt-1 text-[11px] leading-4">{t.label}</figcaption>
          </figure>
        ))}
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** The strongest keypoint's patch and its 128-number descriptor, raw and after RootSIFT. */
export function DescriptorExplainer() {
  const { descriptor } = data.frame;
  const bars = (values: number[], colour: string, from: number) => (
    <svg viewBox="0 0 128 40" preserveAspectRatio="none" className="block h-16 w-full" aria-hidden>
      {values.map((v, i) => (
        <rect key={i} x={i} y={40 - v * 38} width="0.8" height={v * 38} fill={colour} className="explainer-grow" style={delay(from + i * 6)} />
      ))}
    </svg>
  );
  return (
    <Figure caption="Left: the patch around the strongest keypoint. Right: its descriptor, 128 numbers that record how brightness changes in each direction across the patch. Below it, the same numbers after RootSIFT: scale to sum to one, take the square root. The big values shrink and the small ones grow, so no single bin dominates the comparison.">
      <div className="grid grid-cols-[6rem_1fr] items-start gap-4">
        <img src={`${BASE}${descriptor.patch}`} alt="" className="explainer-fade block w-24 rounded border [image-rendering:pixelated]" />
        <div className="min-w-0 space-y-2">
          <div>
            <p className="text-muted-foreground mb-1 text-[11px]">SIFT descriptor, 128 numbers</p>
            {bars(descriptor.raw, "var(--foreground)", 300)}
          </div>
          <div>
            <p className="text-muted-foreground mb-1 text-[11px]">after RootSIFT</p>
            {bars(descriptor.root, WORD[4], 1300)}
          </div>
        </div>
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** Bag of words, on text: two sentences become two histograms. */
export function BowExplainer() {
  const vocabulary = ["wall", "turn", "left", "corridor", "goal", "photo"];
  const docs = [
    { text: "turn left at the wall, then left again down the corridor", counts: [1, 1, 2, 1, 0, 0] },
    { text: "the corridor after the wall: turn left, the goal photo shows it", counts: [1, 1, 1, 1, 1, 1] },
  ];
  return (
    <Figure caption="A bag of words: keep the counts, forget the order. Two sentences with similar counts are about similar things even though no phrase is shared. That is the whole trick, and it works on images once an image has words.">
      <div className="space-y-4">
        {docs.map((doc, d) => (
          <div key={d} className="grid grid-cols-[1fr_auto] items-center gap-4">
            <p className="explainer-fade text-sm leading-6" style={delay(200 + d * 900)}>
              &quot;{doc.text}&quot;
            </p>
            <div className="flex items-end gap-1" aria-hidden>
              {doc.counts.map((n, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className="explainer-grow-y w-5 rounded-t" style={{ height: `${8 + n * 14}px`, background: WORD[i], opacity: n ? 1 : 0.15, ...delay(700 + d * 900 + i * 90) }} />
                  <span className="text-muted-foreground text-[9px]">{vocabulary[i]}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** The frame's keypoints coloured by visual word, and the count per word: its bag of visual words. */
export function BovwExplainer() {
  const { frame, words } = data;
  const max = Math.max(...words.counts);
  return (
    <Figure caption="Every keypoint is named by its nearest visual word and the frame becomes a count per word, exactly like the sentences. Six of the 128 words are coloured here; grey keypoints belong to the others. Two frames of the same place give similar counts. But a count says nothing about where inside a word its descriptors sat.">
      <div className="grid grid-cols-[3fr_2fr] items-center gap-4">
        <div className="relative overflow-hidden rounded-md">
          <img src={`${BASE}${frame.image}`} alt="" className="block w-full" />
          <svg viewBox={`0 0 1 ${frame.height / frame.width}`} className="absolute inset-0 h-full w-full" aria-hidden>
            {frame.keypoints.map(([x, y, size], i) => {
              const w = words.of_keypoint[i];
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={Math.max(size / 2, 0.008)}
                  fill="none"
                  strokeWidth="0.004"
                  className={w >= 0 ? "explainer-tint-stroke" : "stroke-white/50"}
                  style={w >= 0 ? ({ "--tint": WORD[w], ...delay(200 + i * 30) } as CSSProperties) : undefined}
                />
              );
            })}
          </svg>
        </div>
        <div className="flex items-end justify-around" aria-hidden>
          {words.counts.map((n, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <span className="text-[10px] tabular-nums">{n}</span>
              <div className="explainer-grow-y w-6 rounded-t" style={{ height: `${(n / max) * 96}px`, background: WORD[i], ...delay(2200 + i * 120) }} />
            </div>
          ))}
        </div>
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** k blocks of residual sums become one long vector: normalise, concatenate, square-root, normalise. */
export function ConcatExplainer() {
  const blocks = 6;
  const perBlock = 12;
  // A made-up but typical VLAD block: a few large values, many small, some negative.
  const values = Array.from({ length: blocks * perBlock }, (_, i) => {
    const t = Math.sin(i * 1.7) * Math.cos(i * 0.31);
    return Math.sign(t) * Math.pow(Math.abs(t), 1.6);
  });
  const rooted = values.map((v) => Math.sign(v) * Math.sqrt(Math.abs(v)));
  const row = (vals: number[], from: number, label: string) => (
    <div>
      <p className="text-muted-foreground mb-1 text-[11px]">{label}</p>
      <svg viewBox={`0 0 ${blocks * perBlock} 24`} preserveAspectRatio="none" className="block h-14 w-full" aria-hidden>
        <line x1="0" y1="12" x2={blocks * perBlock} y2="12" className="stroke-border" strokeWidth="0.2" />
        {vals.map((v, i) => (
          <rect
            key={i}
            x={i + 0.1}
            y={v >= 0 ? 12 - v * 11 : 12}
            width="0.8"
            height={Math.abs(v) * 11}
            fill={WORD[Math.floor(i / perBlock)]}
            className="explainer-grow"
            style={delay(from + Math.floor(i / perBlock) * 180)}
          />
        ))}
      </svg>
    </div>
  );
  return (
    <Figure caption="Each word's block is normalised and the blocks are laid end to end (six words drawn here; the kit has 128, so 128 × 128 = 16,384 numbers). Then the signed square root of every number, so a few large values cannot dominate, and one last normalisation to unit length. Every frame is now one vector, and the similarity of two frames is their dot product.">
      <div className="space-y-3">
        {row(values, 200, "blocks, one per word, side by side")}
        {row(rooted, 1500, "after the signed square root")}
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** One camera frame and its SIFT keypoints, popping in strongest first. */
export function SiftExplainer() {
  const { frame } = data;
  return (
    <Figure
      caption={
        <>
          The strongest 64 of the {frame.total} SIFT keypoints in this frame. A circle&apos;s size is
          the scale the point was found at; the tick is its main direction. These are the places
          in the image that can be found again from another viewpoint.
        </>
      }
    >
      <div className="relative overflow-hidden rounded-md">
        <img src={`${BASE}${frame.image}`} alt="A camera frame from the robot: a maze corridor with textured walls" className="block w-full" />
        <svg viewBox={`0 0 1 ${frame.height / frame.width}`} className="absolute inset-0 h-full w-full" aria-hidden>
          {frame.keypoints.map(([x, y, size, angle], i) => {
            const r = Math.max(size / 2, 0.008);
            const a = (angle * Math.PI) / 180;
            return (
              <g key={i} className="explainer-pop stroke-chart-2" style={delay(60 + i * 35)} fill="none" strokeWidth="0.0025">
                <circle cx={x} cy={y} r={r} />
                <line x1={x} y1={y} x2={x + Math.cos(a) * r} y2={y + Math.sin(a) * r} />
              </g>
            );
          })}
        </svg>
      </div>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** Descriptors as points; the codebook's centres appear; each point takes its word's colour. */
export function KmeansExplainer() {
  const { points, centres } = data.kmeans;
  return (
    <Figure
      caption={
        <>
          Descriptors from the frame, flattened to two dimensions for the picture. The big dots are
          six of the 128 centres k-means found; each descriptor takes the colour of its nearest
          centre. That centre is its visual word.
        </>
      }
    >
      <svg viewBox="-0.06 -0.06 1.12 0.82" className="block w-full" aria-hidden>
        {points.map(([x, y, c], i) => (
          <g key={i}>
            <line
              x1={x}
              y1={y * 0.7}
              x2={centres[c][0]}
              y2={centres[c][1] * 0.7}
              className="explainer-fade"
              stroke={WORD[c]}
              strokeWidth="0.002"
              opacity="0.4"
              style={delay(1900 + i * 15)}
            />
            <circle
              cx={x}
              cy={y * 0.7}
              r="0.009"
              className="explainer-tint fill-muted-foreground"
              style={{ ...delay(1500 + i * 15), "--tint": WORD[c] } as CSSProperties}
            />
          </g>
        ))}
        {centres.map(([x, y], c) => (
          <g key={c} className="explainer-pop" style={delay(900 + c * 120)}>
            <circle cx={x} cy={y * 0.7} r="0.022" fill={WORD[c]} className="stroke-background" strokeWidth="0.006" />
          </g>
        ))}
      </svg>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** One word's descriptors, their residuals from the centre, and the sum: one block of VLAD. */
export function VladExplainer() {
  const { residuals, sum } = data.vlad;
  const cx = 0.5;
  const cy = 0.4;
  const scale = 1.6;
  const norm = Math.hypot(sum[0], sum[1]) || 1;
  const arrow = [(sum[0] / norm) * 0.28, (sum[1] / norm) * 0.28];
  return (
    <Figure
      caption={
        <>
          One word, the descriptors that fell into it, and the arrow from the centre to each one:
          the residual. Adding the arrows gives v<sub>k</sub>, the word&apos;s block of the VLAD
          vector. A count would only say &quot;eleven&quot;; the sum also says which side of the
          centre they were on.
        </>
      }
    >
      <svg viewBox="0 0 1 0.8" className="block w-full" aria-hidden>
        <circle cx={cx} cy={cy} r="0.02" fill={WORD[0]} />
        {residuals.map(([dx, dy], i) => (
          <g key={i}>
            <line
              x1={cx}
              y1={cy}
              x2={cx + dx * scale}
              y2={cy + dy * scale}
              pathLength={1}
              className="explainer-draw"
              stroke={WORD[0]}
              strokeWidth="0.004"
              opacity="0.7"
              style={delay(400 + i * 110)}
            />
            <circle cx={cx + dx * scale} cy={cy + dy * scale} r="0.009" className="explainer-pop" fill={WORD[0]} style={delay(300 + i * 110)} />
          </g>
        ))}
        <g className="explainer-fade" style={delay(400 + residuals.length * 110 + 300)}>
          <line x1={cx} y1={cy} x2={cx + arrow[0]} y2={cy + arrow[1]} className="stroke-foreground" strokeWidth="0.012" strokeLinecap="round" />
          <circle cx={cx + arrow[0]} cy={cy + arrow[1]} r="0.02" className="fill-foreground" />
          <text x={cx + arrow[0] + 0.04} y={cy + arrow[1] + 0.012} fontSize="0.04" className="fill-foreground">
            v<tspan fontSize="0.028" dy="0.012">k</tspan>
          </text>
        </g>
        <text x={cx + 0.03} y={cy - 0.03} fontSize="0.036" className="fill-muted-foreground">
          c<tspan fontSize="0.026" dy="0.01">k</tspan>
        </text>
      </svg>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/** The query frame against every frame in the database, and the frames it matched. */
export function RetrievalExplainer() {
  const { similarity, trajectories, query, matches, worst } = data.retrieval;
  const n = similarity.length;
  const lo = Math.min(...similarity);
  const hi = Math.max(...similarity);
  const x = (i: number) => (i / (n - 1)) * 1;
  const y = (s: number) => 0.3 - ((s - lo) / (hi - lo)) * 0.26;
  const curve = similarity.map((s, i) => `${x(i).toFixed(4)},${y(s).toFixed(4)}`).join(" ");
  return (
    <Figure
      caption={
        <>
          The query&apos;s vector dotted with every database frame&apos;s, in recording order.
          Green: the three best matches, the same corridor seen on other drives. Red: the worst.
          The numbers are small because the vectors have 16,384 dimensions; what matters is the
          gap between the best and the rest.
        </>
      }
    >
      <div className="grid grid-cols-4 gap-2">
        {[{ image: data.frame.image, label: "query", similarity: null }, ...matches.map((m) => ({ ...m, label: "match" }))].map((m, i) => (
          <figure key={i} className="explainer-fade min-w-0" style={delay(1400 + i * 200)}>
            <img src={`${BASE}${m.image}`} alt="" className="block w-full rounded" />
            <figcaption className="text-muted-foreground mt-1 truncate text-[11px] tabular-nums">
              {m.similarity === null ? "query" : `${m.label} · ${m.similarity.toFixed(3)}`}
            </figcaption>
          </figure>
        ))}
      </div>
      <svg viewBox="-0.02 0 1.04 0.36" className="mt-3 block w-full" aria-hidden>
        {trajectories.slice(1).map(([start]) => (
          <line key={start} x1={x(start)} y1="0.02" x2={x(start)} y2="0.32" className="stroke-border" strokeWidth="0.003" strokeDasharray="0.01 0.008" />
        ))}
        <polyline points={curve} fill="none" pathLength={1} className="explainer-draw stroke-foreground/70" strokeWidth="0.004" />
        <circle cx={x(query)} cy={y(similarity[query])} r="0.012" className="explainer-pop fill-foreground" style={delay(1300)} />
        {matches.map((m, i) => (
          <circle key={m.index} cx={x(m.index)} cy={y(m.similarity)} r="0.012" className="explainer-pop" fill={WORD[2]} style={delay(1400 + i * 200)} />
        ))}
        <circle cx={x(worst.index)} cy={y(worst.similarity)} r="0.01" className="explainer-pop fill-destructive" style={delay(2100)} />
        <text x="0" y="0.355" fontSize="0.028" className="fill-muted-foreground">
          frame 0
        </text>
        <text x="1" y="0.355" fontSize="0.028" textAnchor="end" className="fill-muted-foreground">
          frame {n - 1} · dashed: a new drive begins
        </text>
      </svg>
    </Figure>
  );
}

// ---------------------------------------------------------------------------------------------

/**
 * The baseline's graph over the frames at their true positions, in three stages: the drives
 * as chains of nodes; the shortcut edges between look-alikes; the shortest path to the goal.
 */
export function GraphExplainer({ stage }: { stage: 1 | 2 | 3 }) {
  const { nodes, trajectories, shortcuts, query, goal, path, target } = data.graph;
  const size = (scene.cols - 1) * scene.cell;
  const sx = (x: number) => x;
  const sy = (y: number) => size - y;
  const walls: ReactNode[] = [];
  scene.grid.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      if (row[c] !== "#") continue;
      const x = c * scene.cell;
      const y = r * scene.cell;
      if (row[c + 1] === "#") walls.push(<line key={`h${r}-${c}`} x1={sx(x)} y1={sy(y)} x2={sx(x + scene.cell)} y2={sy(y)} />);
      if (scene.grid[r + 1]?.[c] === "#") walls.push(<line key={`v${r}-${c}`} x1={sx(x)} y1={sy(y)} x2={sx(x)} y2={sy(y + scene.cell)} />);
    }
  });
  const pathPoints = path.map((i) => `${sx(nodes[i][0]).toFixed(3)},${sy(nodes[i][1]).toFixed(3)}`).join(" ");
  const captions = {
    1: "Every kept exploration frame is a node, drawn at its true position so you can see the maze in it. The baseline never has these positions; it only has the chains. Thin edges join consecutive frames of one drive, and each remembers the move recorded between them.",
    2: "Frames from different drives that look alike are probably the same place. The 30 most similar pairs from far apart in the recording get a dashed shortcut edge, weighted by how alike they look. The three drives are now one connected map.",
    3: "Match the current frame to a node (black) and the goal photo to a node (green; the ring is the true goal). Dijkstra's shortest path between them is the plan, and the move recorded on its first edge is the hint the baseline shows.",
  } as const;
  return (
    <Figure caption={captions[stage]}>
      <svg viewBox={`-0.15 -0.15 ${size + 0.3} ${size + 0.3}`} className="block w-full" aria-hidden>
        <g className="stroke-foreground/25" strokeWidth="0.02" strokeLinecap="round">
          {walls}
        </g>
        {trajectories.map(([start, end], t) => (
          <polyline
            key={t}
            points={nodes.slice(start, end).map(([x, y]) => `${sx(x).toFixed(3)},${sy(y).toFixed(3)}`).join(" ")}
            fill="none"
            pathLength={1}
            className={cn("stroke-muted-foreground/60", stage === 1 && "explainer-draw")}
            strokeWidth="0.015"
            style={delay(300 + t * 500)}
          />
        ))}
        {nodes.map(([x, y], i) => (
          <circle key={i} cx={sx(x)} cy={sy(y)} r="0.035" className={cn("fill-muted-foreground/70", stage === 1 && "explainer-pop")} style={stage === 1 ? delay(200 + i * 8) : undefined} />
        ))}
        {stage >= 2
          ? shortcuts.map(([a, b], i) => (
              <line
                key={i}
                x1={sx(nodes[a][0])}
                y1={sy(nodes[a][1])}
                x2={sx(nodes[b][0])}
                y2={sy(nodes[b][1])}
                className={cn(stage === 2 && "explainer-fade")}
                stroke={WORD[1]}
                strokeWidth="0.014"
                strokeDasharray="0.05 0.04"
                opacity="0.7"
                style={stage === 2 ? delay(300 + i * 80) : undefined}
              />
            ))
          : null}
        {stage === 3 ? (
          <>
            <polyline points={pathPoints} fill="none" pathLength={1} className="explainer-draw stroke-primary" strokeWidth="0.06" strokeLinecap="round" strokeLinejoin="round" style={delay(900)} />
            <circle cx={sx(nodes[query][0])} cy={sy(nodes[query][1])} r="0.1" className="explainer-pop fill-foreground" style={delay(200)} />
            <circle cx={sx(nodes[goal][0])} cy={sy(nodes[goal][1])} r="0.1" className="explainer-pop" fill={WORD[2]} style={delay(500)} />
            <circle cx={sx(target[0])} cy={sy(target[1])} r="0.14" fill="none" stroke={WORD[2]} strokeWidth="0.03" className="explainer-pop" style={delay(500)} />
            <text x={sx(nodes[query][0]) + 0.16} y={sy(nodes[query][1]) + 0.05} fontSize="0.22" className="fill-foreground">
              now
            </text>
            <text x={sx(nodes[goal][0]) + 0.18} y={sy(nodes[goal][1]) + 0.05} fontSize="0.22" className="fill-foreground">
              goal
            </text>
          </>
        ) : null}
      </svg>
    </Figure>
  );
}
